import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Radio,
  Trash2,
  XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const SEVERITY_COLORS = {
  P1: "border-rose-500/40 text-rose-300 bg-rose-500/5",
  P2: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  P3: "border-sky-500/40 text-sky-300 bg-sky-500/5",
  P4: "border-zinc-500/40 text-zinc-300 bg-zinc-500/5",
};

export const STATUS_STYLE = {
  pending_analysis: "text-zinc-400",
  analyzing: "text-blue-300",
  awaiting_approval: "text-amber-300",
  approved: "text-emerald-300",
  rejected: "text-rose-300",
  modified: "text-violet-300",
};

function expiresLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "expired";
  const hrs = Math.round(ms / 3_600_000);
  if (hrs < 24) return `expires in ${hrs}h`;
  const days = Math.round(hrs / 24);
  return `expires in ${days}d`;
}

export function IncidentRow({ inc, user, onRefresh, onDelete }) {
  return (
    <div
      data-testid={`incident-row-${inc.id}`}
      className="p-5 flex items-start gap-3 hover:bg-white/[0.02] transition-colors"
    >
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <Badge
          variant="outline"
          className={`rounded-sm font-mono text-[10px] ${SEVERITY_COLORS[inc.severity] || ""}`}
        >
          {inc.severity}
        </Badge>
        <div className="flex-1 min-w-0">
          <Link
            to={`/incidents/${inc.id}`}
            className="block text-sm text-zinc-100 font-medium truncate hover:text-white"
          >
            {inc.title}
            {inc.archived ? (
              <span className="ml-2 text-[10px] font-mono uppercase text-zinc-500">
                · archived
              </span>
            ) : null}
          </Link>
          <div className="mt-0.5 text-xs text-zinc-500 font-mono truncate">
            {inc.affected_system}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] font-mono flex-wrap">
            <span className={STATUS_STYLE[inc.status] || "text-zinc-400"}>
              · {inc.status.replace(/_/g, " ")}
            </span>
            <span className="text-zinc-600">
              · {new Date(inc.created_at).toLocaleString()}
            </span>
            {inc.user_id && inc.expires_at ? (
              <span
                data-testid={`expires-${inc.id}`}
                className="inline-flex items-center gap-1 text-amber-300/90"
              >
                <Clock3 className="h-3 w-3" strokeWidth={1.75} />
                {expiresLabel(inc.expires_at)}
              </span>
            ) : null}
          </div>
        </div>
        <Link to={`/incidents/${inc.id}`} className="mt-1 shrink-0">
          {inc.status === "approved" ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          ) : inc.status === "rejected" ? (
            <XCircle className="h-4 w-4 text-rose-400" />
          ) : (
            <Radio className="h-4 w-4 text-blue-400 animate-pulse" />
          )}
        </Link>
      </div>
      {inc.archived ? (
        <button
          onClick={async () => {
            try {
              await api.unarchive(inc.id);
              toast.success("Restored from archive");
              onRefresh();
            } catch (_) {
              toast.error("Action failed");
            }
          }}
          data-testid={`archive-btn-${inc.id}`}
          title="Restore from archive"
          className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-white/5 rounded-sm transition-colors"
        >
          <ArchiveRestore className="h-4 w-4" strokeWidth={1.5} />
        </button>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              data-testid={`archive-btn-${inc.id}`}
              title="Archive"
              className="p-1.5 text-zinc-500 hover:text-zinc-200 hover:bg-white/5 rounded-sm transition-colors"
            >
              <Archive className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent
            data-testid="dashboard-archive-dialog"
            className="cre-surface border-white/10"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">
                Archive this incident?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                It will be removed from active/recent views but retained in
                archived history with its audit trail.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid={`archive-cancel-${inc.id}`}
                className="rounded-sm border-white/15"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid={`archive-confirm-${inc.id}`}
                onClick={async () => {
                  try {
                    await api.archive(inc.id);
                    toast.success("Incident archived");
                    onRefresh();
                  } catch (_) {
                    toast.error("Action failed");
                  }
                }}
                className="rounded-sm bg-rose-500 hover:bg-rose-400 text-white"
              >
                Archive incident
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {user && user.id === inc.user_id ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              data-testid={`delete-btn-${inc.id}`}
              title="Delete this saved analysis"
              className="p-1.5 text-zinc-500 hover:text-rose-300 hover:bg-rose-500/5 rounded-sm transition-colors"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent
            data-testid="dashboard-delete-dialog"
            className="cre-surface border-white/10"
          >
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">
                Delete this saved analysis?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                This permanently removes the incident, its runbook, and audit
                trail from your saved history. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid={`delete-cancel-${inc.id}`}
                className="rounded-sm border-white/15"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid={`delete-confirm-${inc.id}`}
                onClick={() => onDelete(inc.id)}
                className="rounded-sm bg-rose-500 hover:bg-rose-400 text-white"
              >
                Delete permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
      <Link
        to={`/incidents/${inc.id}`}
        className="p-1.5 text-zinc-500 hover:text-zinc-200"
      >
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
